import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import { createPDF, autoTableDefaults } from '../pdfUtils';

function cellText(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'boolean') return val ? 'Evet' : 'Hayır';
  if (val instanceof Date) return val.toLocaleDateString('tr-TR');
  if (React.isValidElement(val)) return '';
  return String(val);
}

function exportExcel(columns, rows, filename) {
  const visibleCols = columns.filter(c => !c.hidden);
  const header = visibleCols.map(c => c.label);
  const data = rows.map(r =>
    visibleCols.map(c => {
      const raw = c.exportValue ? c.exportValue(r) : (c.key ? r[c.key] : '');
      return cellText(raw);
    })
  );
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  ws['!cols'] = visibleCols.map(c => ({ wch: Math.max(c.label.length + 4, 14) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Veri');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

function exportPDF(columns, rows, filename, title) {
  const visibleCols = columns.filter(c => !c.hidden);
  const doc = createPDF({ orientation: visibleCols.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14); doc.setFont('Arial', 'bold');
  doc.text(title || filename, 14, 15);
  doc.setFontSize(9); doc.setFont('Arial', 'normal');
  doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}  |  Toplam: ${rows.length} kayit`, 14, 22);
  autoTable(doc, {
    startY: 27,
    head: [visibleCols.map(c => c.label)],
    body: rows.map(r =>
      visibleCols.map(c => {
        const raw = c.exportValue ? c.exportValue(r) : (c.key ? r[c.key] : '');
        return cellText(raw);
      })
    ),
    ...autoTableDefaults,
    margin: { left: 14, right: 14 },
  });
  doc.save(`${filename}.pdf`);
}

// ─── Kolon Seçici Panel ───────────────────────────────────────────────────
function KolonSecici({ cols, onToggle, onReset, btnRef, wrapText, onToggleWrap }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (btnRef.current && panelRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const pw = panelRef.current.offsetWidth;
      setPos({ top: r.bottom + window.scrollY + 4, left: Math.max(4, r.right - pw + window.scrollX) });
    }
  }, [btnRef]);

  const secilebilir = cols.filter(c => c.key && c.key !== '_islem' && c.key !== '_no' && c.key !== '_detay' && c.key !== '_select');
  const gorunenSayisi = secilebilir.filter(c => !c.hidden).length;

  return (
    <div ref={panelRef} style={{
      position: 'fixed', top: pos.top, left: pos.left,
      background: '#fff', border: '1px solid #c5cae9', borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 9999,
      minWidth: '220px', maxWidth: '280px', overflow: 'hidden',
    }}>
      <div style={{ background: '#1a237e', color: '#fff', padding: '10px 14px', fontSize: '13px', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Kolon Görünürlüğü</span>
        <span style={{ fontSize: '11px', opacity: 0.8 }}>{gorunenSayisi}/{secilebilir.length} aktif</span>
      </div>
      <div style={{ padding: '6px 0', maxHeight: '340px', overflowY: 'auto' }}>
        {secilebilir.map(col => (
          <label key={col.key} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '7px 14px', cursor: 'pointer', fontSize: '13px',
            background: col.hidden ? '#fafafa' : '#fff',
            borderLeft: col.hidden ? '3px solid transparent' : '3px solid #3949ab',
          }}
            onMouseOver={e => e.currentTarget.style.background = '#e8eaf6'}
            onMouseOut={e => e.currentTarget.style.background = col.hidden ? '#fafafa' : '#fff'}
          >
            <input type="checkbox" checked={!col.hidden} onChange={() => onToggle(col.key)}
              style={{ accentColor: '#1a237e', width: '15px', height: '15px', cursor: 'pointer' }} />
            <span style={{ color: col.hidden ? '#999' : '#222' }}>{col.label}</span>
          </label>
        ))}
      </div>
      <label style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px',
        cursor: 'pointer', fontSize: '12px', borderTop: '1px solid #eee', color: '#303f9f', fontWeight: '600',
      }}>
        <input type="checkbox" checked={wrapText} onChange={onToggleWrap}
          style={{ accentColor: '#1a237e', width: '15px', height: '15px', cursor: 'pointer' }} />
        <span>Hücre metinlerini kaydır</span>
      </label>
      <div style={{ padding: '8px 14px', borderTop: '1px solid #eee', display: 'flex', gap: '6px' }}>
        <button onClick={() => secilebilir.forEach(c => c.hidden && onToggle(c.key))}
          style={{ flex: 1, fontSize: '11px', padding: '5px', background: '#e8eaf6', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#1a237e' }}>
          Tümünü Göster
        </button>
        <button onClick={onReset}
          style={{ flex: 1, fontSize: '11px', padding: '5px', background: '#fce4ec', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
          Varsayılana Dön
        </button>
      </div>
    </div>
  );
}

// ─── Sağ tık context menu ─────────────────────────────────────────────────
function KolonContextMenu({ x, y, colKey, colLabel, onHide, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [onClose]);
  return (
    <div ref={ref} style={{
      position: 'fixed', top: y, left: x,
      background: '#fff', border: '1px solid #c5cae9', borderRadius: '6px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 99999, minWidth: '180px', overflow: 'hidden',
    }}>
      <div style={{ background: '#1a237e', color: '#fff', padding: '7px 12px', fontSize: '12px', fontWeight: '600' }}>{colLabel}</div>
      <button onClick={onHide}
        style={{ display: 'block', width: '100%', padding: '8px 14px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: '#c62828' }}
        onMouseOver={e => e.currentTarget.style.background = '#fce4ec'}
        onMouseOut={e => e.currentTarget.style.background = 'none'}
      >🚫 Bu kolonu gizle</button>
      <button onClick={onClose}
        style={{ display: 'block', width: '100%', padding: '8px 14px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: '#555' }}
        onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
        onMouseOut={e => e.currentTarget.style.background = 'none'}
      >✕ Kapat</button>
    </div>
  );
}

// ─── Excel Stili Filtre Paneli ────────────────────────────────────────────
function ExcelFilterPanel({ col, allData, currentFilter, onApply, onClose, anchorRef }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState('');

  const uniqueVals = useMemo(() => {
    const vals = new Set();
    allData.forEach(row => {
      const v = col.exportValue ? col.exportValue(row) : row[col.key];
      const t = cellText(v);
      vals.add(t || '(Boş)');
    });
    return [...vals].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [allData, col]);

  const [selected, setSelected] = useState(() =>
    currentFilter ? new Set(currentFilter) : new Set(uniqueVals)
  );

  useEffect(() => {
    if (!currentFilter) setSelected(new Set(uniqueVals));
  }, [uniqueVals]); // eslint-disable-line

  const filtered = search
    ? uniqueVals.filter(v => v.toLowerCase().includes(search.toLowerCase()))
    : uniqueVals;

  const allFilteredChecked = filtered.every(v => selected.has(v));
  const someFilteredChecked = filtered.some(v => selected.has(v));

  useLayoutEffect(() => {
    if (!anchorRef?.current || !panelRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const pw = panelRef.current.offsetWidth || 220;
    const ph = panelRef.current.offsetHeight || 300;
    let top = r.bottom + 4;
    if (top + ph > window.innerHeight - 8) top = r.top - ph - 4;
    let left = r.left;
    if (left + pw > window.innerWidth - 8) left = window.innerWidth - pw - 8;
    setPos({ top: Math.max(4, top), left: Math.max(4, left) });
  }, [anchorRef]);

  useEffect(() => {
    const close = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Slight delay so the opening click doesn't immediately close
    const t = setTimeout(() => document.addEventListener('mousedown', close), 100);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [onClose, anchorRef]);

  const toggle = (val) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(val)) s.delete(val); else s.add(val);
      return s;
    });
  };

  const toggleAll = () => {
    if (allFilteredChecked) {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(v => s.delete(v)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); filtered.forEach(v => s.add(v)); return s; });
    }
  };

  const indetermRef = useRef(null);
  useEffect(() => {
    if (indetermRef.current) {
      indetermRef.current.indeterminate = !allFilteredChecked && someFilteredChecked;
    }
  }, [allFilteredChecked, someFilteredChecked]);

  const apply = () => {
    if (selected.size === 0) { onApply(new Set(['__NONE__'])); onClose(); return; }
    if (selected.size >= uniqueVals.length) { onApply(null); }
    else { onApply(new Set(selected)); }
    onClose();
  };

  return (
    <div ref={panelRef} style={{
      position: 'fixed', top: pos.top, left: pos.left,
      background: '#fff', border: '1px solid #c5cae9', borderRadius: '6px',
      boxShadow: '0 6px 24px rgba(0,0,0,0.18)', zIndex: 99998,
      minWidth: '220px', maxWidth: '300px',
    }}>
      <div style={{ padding: '7px 8px', borderBottom: '1px solid #eee' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Ara..."
          autoFocus
          style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', background: '#f9f9f9', borderBottom: '1px solid #eee' }}
          onMouseOver={e => e.currentTarget.style.background = '#e8eaf6'}
          onMouseOut={e => e.currentTarget.style.background = '#f9f9f9'}
        >
          <input ref={indetermRef} type="checkbox" checked={allFilteredChecked} onChange={toggleAll} style={{ accentColor: '#1a237e', width: '14px', height: '14px' }} />
          (Tümünü Seç)
        </label>
        {filtered.map(val => (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}
            onMouseOver={e => e.currentTarget.style.background = '#f5f5ff'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <input type="checkbox" checked={selected.has(val)} onChange={() => toggle(val)}
              style={{ accentColor: '#1a237e', width: '14px', height: '14px', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
          </label>
        ))}
        {filtered.length === 0 && <div style={{ padding: '10px', color: '#999', fontSize: '12px', textAlign: 'center' }}>Sonuç yok</div>}
      </div>
      <div style={{ display: 'flex', gap: '5px', padding: '7px 8px', borderTop: '1px solid #eee' }}>
        <button onClick={() => { onApply(null); onClose(); }}
          style={{ flex: 1, padding: '5px', fontSize: '11px', background: '#fce4ec', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828' }}>
          Temizle
        </button>
        <button onClick={onClose}
          style={{ flex: 1, padding: '5px', fontSize: '11px', background: '#f5f5f5', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          İptal
        </button>
        <button onClick={apply}
          style={{ flex: 1, padding: '5px', fontSize: '11px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
          Tamam
        </button>
      </div>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '1000', value: 1000 },
  { label: 'Tümü', value: 0 },
];

// ─── Ana DataTable bileşeni ────────────────────────────────────────────────
export default function DataTable({
  columns: initialColumns,
  data,
  filename = 'veri',
  pdfTitle,
  loading = false,
  toolbar,
  rowKey = 'id',
  onRowClick,
  stickyHeader = true,
  maxHeight = '70vh',
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  getRowStyle,
  defaultWrapText = false,
}) {
  const storageKey = `dt_cols_${filename}`;
  const filterKey = `dt_filters_${filename}`;
  const wrapKey = `dt_wrap_${filename}`;

  const [cols, setCols] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedMap = JSON.parse(saved);
        return initialColumns
          .map((c, i) => ({ ...c, _order: savedMap[c.key]?._order ?? i, hidden: savedMap[c.key] !== undefined ? savedMap[c.key].hidden : c.hidden }))
          .sort((a, b) => a._order - b._order);
      }
    } catch {}
    return initialColumns.map((c, i) => ({ ...c, _order: i }));
  });

  const [sortKey, setSortKey] = useState(() => { try { return JSON.parse(sessionStorage.getItem(filterKey+'_sort'))?.key ?? null; } catch { return null; } });
  const [sortDir, setSortDir] = useState(() => { try { return JSON.parse(sessionStorage.getItem(filterKey+'_sort'))?.dir ?? 'asc'; } catch { return 'asc'; } });
  const [showKolonSecici, setShowKolonSecici] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [dragOver, setDragOver] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [wrapText, setWrapText] = useState(() => {
    try {
      const saved = localStorage.getItem(wrapKey);
      return saved === null ? defaultWrapText : saved === 'true';
    } catch { return defaultWrapText; }
  });
  const [excelFilters, setExcelFilters] = useState(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(filterKey));
      if (!raw || typeof raw !== 'object') return {};
      const restored = {};
      Object.entries(raw).forEach(([k, v]) => {
        if (Array.isArray(v)) restored[k] = new Set(v);
        // Dizi değilse (eski bozuk {} verisi vb.) bu anahtarı atla
      });
      return restored;
    } catch { return {}; }
  });
  const [activeExcelFilter, setActiveExcelFilter] = useState(null);
  // Sayfalama — sayfa yenilenince 10'a döner (sessionStorage yok, sadece state)
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const dragSrc = useRef(null);
  const kolonBtnRef = useRef(null);
  const filterBtnRefs = useRef({});

  // ─ Sütun genişliği (px) — null = otomatik ────────────────────────────────
  const colWidthKey = `dt_colwidths_${filename}`;
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(colWidthKey)) || {}; } catch { return {}; }
  });
  const resizeState = useRef(null); // { key, startX, startW }

  const startResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startW = th.offsetWidth;
    resizeState.current = { key: col.key, startX: e.clientX, startW };

    const onMove = (mv) => {
      const delta = mv.clientX - resizeState.current.startX;
      const newW = Math.max(40, resizeState.current.startW + delta);
      setColWidths(prev => ({ ...prev, [resizeState.current.key]: newW }));
    };
    const onUp = () => {
      resizeState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const autoFitCol = useCallback((col) => {
    const values = data.slice(0, 250).map(row => {
      const raw = col.exportValue ? col.exportValue(row) : row[col.key];
      return cellText(raw);
    });
    const longest = [col.label || '', ...values].reduce((a, b) => a.length >= b.length ? a : b, '');
    const estimated = Math.min(520, Math.max(80, longest.length * 7.2 + 56));
    setColWidths(prev => ({ ...prev, [col.key]: Math.round(estimated) }));
  }, [data]);

  useEffect(() => {
    try { localStorage.setItem(colWidthKey, JSON.stringify(colWidths)); } catch {}
  }, [colWidths, colWidthKey]);

  useEffect(() => {
    try { localStorage.setItem(wrapKey, String(wrapText)); } catch {}
  }, [wrapText, wrapKey]);

  useEffect(() => {
    try {
      const toSave = {};
      cols.forEach((c, i) => { if (c.key) toSave[c.key] = { hidden: !!c.hidden, _order: i }; });
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch {}
  }, [cols, storageKey]);

  useEffect(() => {
    try {
      // Set nesnelerini diziye çevir (JSON.stringify Set'i {} olarak serialize eder)
      const serializable = {};
      Object.entries(excelFilters).forEach(([k, v]) => { serializable[k] = v instanceof Set ? [...v] : v; });
      sessionStorage.setItem(filterKey, JSON.stringify(serializable));
    } catch {}
  }, [excelFilters, filterKey]);
  useEffect(() => { try { sessionStorage.setItem(filterKey+'_sort', JSON.stringify({ key: sortKey, dir: sortDir })); } catch {} }, [sortKey, sortDir, filterKey]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedMap = JSON.parse(saved);
        setCols(initialColumns
          .map((c, i) => ({ ...c, _order: savedMap[c.key]?._order ?? i, hidden: savedMap[c.key] !== undefined ? savedMap[c.key].hidden : c.hidden }))
          .sort((a, b) => a._order - b._order));
        return;
      }
    } catch {}
    setCols(initialColumns.map((c, i) => ({ ...c, _order: i })));
  }, [initialColumns.length]); // eslint-disable-line

  useEffect(() => {
    if (!showKolonSecici) return;
    const close = (e) => { if (kolonBtnRef.current && !kolonBtnRef.current.contains(e.target)) setShowKolonSecici(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showKolonSecici]);

  const toggleKolon = useCallback((key) => {
    setCols(prev => prev.map(c => c.key === key ? { ...c, hidden: !c.hidden } : c));
  }, []);

  const resetKolonlar = useCallback(() => {
    try { localStorage.removeItem(storageKey); localStorage.removeItem(colWidthKey); localStorage.removeItem(wrapKey); } catch {}
    setCols(initialColumns.map((c, i) => ({ ...c, _order: i })));
    setExcelFilters({});
    setColWidths({});
    setWrapText(defaultWrapText);
  }, [initialColumns, storageKey, colWidthKey, wrapKey, defaultWrapText]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const onDragStart = useCallback((e, idx) => {
    dragSrc.current = cols.filter(c => !c.hidden)[idx]?.key ?? null;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }, [cols]);
  const onDragOver = useCallback((e, idx) => { e.preventDefault(); setDragOver(idx); }, []);
  const onDrop = useCallback((e, idx) => {
    e.preventDefault();
    const srcKey = dragSrc.current;
    const destKey = cols.filter(c => !c.hidden)[idx]?.key;
    if (srcKey == null || destKey == null || srcKey === destKey) { dragSrc.current = null; setDragOver(null); return; }
    setCols(prev => {
      const next = [...prev];
      const visiblePositions = next.reduce((positions, col, i) => {
        if (!col.hidden) positions.push(i);
        return positions;
      }, []);
      const visible = visiblePositions.map(i => next[i]);
      const src = visible.findIndex(col => col.key === srcKey);
      const dest = visible.findIndex(col => col.key === destKey);
      if (src < 0 || dest < 0) return prev;
      const [moved] = visible.splice(src, 1);
      visible.splice(dest, 0, moved);
      visiblePositions.forEach((position, i) => { next[position] = visible[i]; });
      return next.map((c, i) => ({ ...c, _order: i }));
    });
    dragSrc.current = null; setDragOver(null);
  }, [cols]);

  // Filtre veya data değişince sayfa 1'e dön
  useEffect(() => { setCurrentPage(1); }, [excelFilters, data]);

  // ─ Veri işleme: excel filtreler + sıralama ───────────────────────────────
  const processed = useMemo(() => {
    let rows = [...data];
    Object.entries(excelFilters).forEach(([key, valSet]) => {
      if (!valSet || typeof valSet.has !== 'function') return;
      const col = cols.find(c => c.key === key);
      rows = rows.filter(r => {
        const v = col?.exportValue ? col.exportValue(r) : r[key];
        const t = cellText(v) || '(Boş)';
        return valSet.has(t);
      });
    });
    Object.entries(columnFilters).forEach(([key, value]) => {
      const needle = value.trim().toLocaleLowerCase('tr-TR');
      if (!needle) return;
      const col = cols.find(c => c.key === key);
      rows = rows.filter(r => {
        const raw = col?.exportValue ? col.exportValue(r) : r[key];
        return cellText(raw).toLocaleLowerCase('tr-TR').includes(needle);
      });
    });
    if (sortKey) {
      const col = cols.find(c => c.key === sortKey);
      rows.sort((a, b) => {
        const va = col?.exportValue ? col.exportValue(a) : a[sortKey];
        const vb = col?.exportValue ? col.exportValue(b) : b[sortKey];
        const sa = cellText(va).toLowerCase();
        const sb = cellText(vb).toLowerCase();
        const numA = parseFloat(sa), numB = parseFloat(sb);
        const cmp = (!isNaN(numA) && !isNaN(numB)) ? numA - numB : sa.localeCompare(sb, 'tr');
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, excelFilters, columnFilters, sortKey, sortDir, cols]);

  // ─ Sayfalama hesabı ──────────────────────────────────────────────────────
  const totalRows = processed.length;
  const effectivePageSize = pageSize === 0 ? totalRows : pageSize;
  const totalPages = effectivePageSize > 0 ? Math.ceil(totalRows / effectivePageSize) : 1;
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const pagedRows = pageSize === 0
    ? processed
    : processed.slice((safePage - 1) * effectivePageSize, safePage * effectivePageSize);

  const visibleCols = cols.filter(c => !c.hidden);

  // ─ Satır seçim ────────────────────────────────────────────────────────────
  const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const allVisibleSelected = pagedRows.length > 0 && pagedRows.every(r => selectedSet.has(r[rowKey]));
  const someSelected = pagedRows.some(r => selectedSet.has(r[rowKey]));
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = !allVisibleSelected && someSelected;
  }, [allVisibleSelected, someSelected]);

  const toggleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allVisibleSelected) {
      const ids = pagedRows.map(r => r[rowKey]);
      onSelectionChange(selectedRows.filter(id => !ids.includes(id)));
    } else {
      const ids = pagedRows.map(r => r[rowKey]);
      onSelectionChange([...new Set([...selectedRows, ...ids])]);
    }
  };

  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    if (selectedSet.has(id)) onSelectionChange(selectedRows.filter(x => x !== id));
    else onSelectionChange([...selectedRows, id]);
  };

  const activeFilterCount = Object.values(excelFilters).filter(Boolean).length;
  const hiddenColCount = cols.filter(c => c.hidden && c.key && !['_islem','_no','_detay','_select'].includes(c.key)).length;

  // Sayfa numaraları
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const delta = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= safePage - delta && i <= safePage + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  }, [totalPages, safePage]);

  return (
    <div>
      {/* ─── Araç çubuğu ─────────────────────────────────────────────── */}
      <div className="datatable-main-toolbar">
        {toolbar}
        <div className="datatable-actions">
          <button className="btn btn-outline btn-sm"
            onClick={() => setShowColumnFilters(f => !f)}
            style={{ fontSize: '12px', background: showColumnFilters ? '#e8eaf6' : undefined }}
            title="Kolon başlıklarının altında filtre alanlarını göster/gizle"
            aria-expanded={showColumnFilters}
          >
            Filtrele{Object.values(columnFilters).filter(v => v.trim()).length > 0 && ` (${Object.values(columnFilters).filter(v => v.trim()).length})`}
          </button>
          <div ref={kolonBtnRef} style={{ position: 'relative' }}>
            <button className="btn btn-outline btn-sm"
              onClick={() => setShowKolonSecici(f => !f)}
              style={{ fontSize: '12px', background: showKolonSecici ? '#e8eaf6' : undefined }}
              title="Kolonları göster/gizle"
            >
              ⚙️ Kolonlar
              {hiddenColCount > 0 && (
                <span style={{ marginLeft: '4px', background: '#f44336', color: '#fff', borderRadius: '8px', padding: '1px 5px', fontSize: '10px' }}>{hiddenColCount}</span>
              )}
            </button>
            {showKolonSecici && <KolonSecici cols={cols} onToggle={toggleKolon} onReset={resetKolonlar} btnRef={kolonBtnRef} wrapText={wrapText} onToggleWrap={() => setWrapText(v => !v)} />}
          </div>
          <button className="btn btn-outline btn-sm"
            onClick={() => exportExcel(cols, processed, filename)}
            style={{ fontSize: '12px', color: '#1b5e20', borderColor: '#1b5e20' }}
            title="Excel olarak indir"
          >📊 Excel</button>
          <button className="btn btn-outline btn-sm"
            onClick={() => exportPDF(cols, processed, filename, pdfTitle)}
            style={{ fontSize: '12px', color: '#b71c1c', borderColor: '#b71c1c' }}
            title="PDF olarak indir"
          >📄 PDF</button>
        </div>
      </div>

      {/* ─── Context Menu ──────────────────────────────────────────────── */}
      {contextMenu && (
        <KolonContextMenu
          x={contextMenu.x} y={contextMenu.y}
          colKey={contextMenu.colKey} colLabel={contextMenu.colLabel}
          onHide={() => { toggleKolon(contextMenu.colKey); setContextMenu(null); }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* ─── Excel Filtre Paneli ────────────────────────────────────────── */}
      {activeExcelFilter && (() => {
        const col = cols.find(c => c.key === activeExcelFilter.colKey);
        if (!col) return null;
        return (
          <ExcelFilterPanel
            col={col}
            allData={data}
            currentFilter={excelFilters[activeExcelFilter.colKey]}
            onApply={(val) => {
              setExcelFilters(f => {
                const next = { ...f };
                if (val === null) delete next[activeExcelFilter.colKey];
                else next[activeExcelFilter.colKey] = val;
                return next;
              });
            }}
            onClose={() => setActiveExcelFilter(null)}
            anchorRef={activeExcelFilter.anchorRef}
          />
        );
      })()}

      {/* ─── Aktif filtre göstergesi ─────────────────────────────────────── */}
      {activeFilterCount > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: '#888' }}>Aktif filtreler:</span>
          {Object.entries(excelFilters).filter(([, v]) => v).map(([key, valSet]) => {
            const col = cols.find(c => c.key === key);
            return (
              <span key={key} style={{ fontSize: '11px', background: '#e8eaf6', borderRadius: '10px', padding: '2px 8px', color: '#1a237e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {col?.label}: {valSet.size} seçili
                <button onClick={() => setExcelFilters(f => { const n = {...f}; delete n[key]; return n; })}
                  style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', padding: '0', fontSize: '12px', lineHeight: 1 }}>×</button>
              </span>
            );
          })}
          <button onClick={() => setExcelFilters({})}
            style={{ fontSize: '11px', background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', padding: 0 }}>
            ✕ Tümünü temizle
          </button>
        </div>
      )}

      {/* ─── Tablo ──────────────────────────────────────────────────────── */}
      <div className="table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
          <colgroup>
            {selectable && <col style={{ width: '36px' }} />}
            {visibleCols.map((col, idx) => (
              <col key={col.key || idx} style={{ width: colWidths[col.key] ?? col.width ?? 'auto' }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '36px', position: 'sticky', top: 0, zIndex: 2, textAlign: 'center' }}>
                  <input ref={selectAllRef} type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                    style={{ accentColor: '#fff', width: '14px', height: '14px', cursor: 'pointer' }} />
                </th>
              )}
              {visibleCols.map((col, idx) => {
                const hasFil = !!(excelFilters[col.key]);
                const thWidth = colWidths[col.key] ?? col.width;
                if (!filterBtnRefs.current[col.key]) filterBtnRefs.current[col.key] = React.createRef();
                return (
                  <th
                    key={col.key || idx}
                    draggable
                    onDragStart={e => onDragStart(e, idx)}
                    onDragOver={e => onDragOver(e, idx)}
                    onDrop={e => onDrop(e, idx)}
                    onDragLeave={() => setDragOver(null)}
                    onContextMenu={col.key && !['_islem','_no','_detay'].includes(col.key) ? (e) => {
                      e.preventDefault();
                      setContextMenu({ x: e.clientX, y: e.clientY, colKey: col.key, colLabel: col.label });
                    } : undefined}
                    style={{
                      cursor: col.sortable !== false ? 'pointer' : 'grab',
                      userSelect: 'none', whiteSpace: 'nowrap',
                      background: dragOver === idx ? '#c5cae9' : undefined,
                      width: thWidth, minWidth: thWidth, maxWidth: thWidth,
                      position: 'sticky', top: 0, zIndex: 2,
                      overflow: 'hidden', boxSizing: 'border-box',
                    }}
                    onClick={() => col.sortable !== false && col.key && handleSort(col.key)}
                    title={col.sortable !== false ? `Sırala: ${col.label}` : `Sürükle: ${col.label}`}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', width: '100%', position: 'relative' }}>
                      <span style={{ fontSize: '10px', color: '#9fa8da', cursor: 'grab' }}>⠿</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label}</span>
                      {col.sortable !== false && col.key && (
                        <span style={{ fontSize: '10px', color: sortKey === col.key ? '#1a237e' : '#9fa8da' }}>
                          {sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      )}
                      {col.key && !['_islem','_no','_detay'].includes(col.key) && (
                        <span
                          ref={filterBtnRefs.current[col.key]}
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (activeExcelFilter?.colKey === col.key) {
                              setActiveExcelFilter(null);
                            } else {
                              setActiveExcelFilter({ colKey: col.key, anchorRef: filterBtnRefs.current[col.key] });
                            }
                          }}
                          title="Değere göre filtrele"
                          style={{
                            fontSize: '10px', width: '18px', height: '18px', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', borderRadius: '3px',
                            background: hasFil ? '#ffd54f' : 'rgba(26,35,126,0.12)',
                            color: hasFil ? '#333' : '#5c6bc0',
                            cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s',
                            border: hasFil ? '1px solid #f9a825' : '1px solid rgba(26,35,126,0.2)',
                          }}
                          onMouseOver={e => { if (!hasFil) e.currentTarget.style.background = 'rgba(26,35,126,0.22)'; }}
                          onMouseOut={e => { if (!hasFil) e.currentTarget.style.background = 'rgba(26,35,126,0.12)'; }}
                        >
                          ▾
                        </span>
                      )}
                      {/* Resize handle — sütun sağ kenarı */}
                      {col.key && (
                        <span
                          onMouseDown={e => startResize(e, col)}
                          onDoubleClick={e => { e.stopPropagation(); autoFitCol(col); }}
                          title="Genişliği ayarla (sürükle) · Çift tıkla: içeriğe göre genişlet"
                          style={{
                            position: 'absolute', right: 0, top: 0, bottom: 0,
                            width: '6px', cursor: 'col-resize',
                            background: 'rgba(26,35,126,0.15)',
                            borderLeft: '1px solid rgba(26,35,126,0.25)',
                            flexShrink: 0, zIndex: 1,
                          }}
                          onMouseOver={e => { e.currentTarget.style.background = 'rgba(26,35,126,0.4)'; }}
                          onMouseOut={e => { e.currentTarget.style.background = 'rgba(26,35,126,0.15)'; }}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
            {showColumnFilters && (
              <tr>
                {selectable && <th style={{ position: 'sticky', top: '36px', zIndex: 2, background: '#f5f6fb' }} />}
                {visibleCols.map((col, idx) => (
                  <th key={col.key || idx} style={{
                    position: 'sticky', top: '36px', zIndex: 2, background: '#f5f6fb',
                    padding: '4px', width: colWidths[col.key] ?? col.width,
                    minWidth: colWidths[col.key] ?? col.width, maxWidth: colWidths[col.key] ?? col.width,
                  }}>
                    {col.key && col.filterable !== false && !['_islem','_no','_detay','_select'].includes(col.key) && (
                      <input
                        type="search"
                        value={columnFilters[col.key] || ''}
                        onChange={e => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        placeholder="Filtrele..."
                        aria-label={`${col.label} filtresi`}
                        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '5px 6px', border: '1px solid #c5cae9', borderRadius: '4px', fontSize: '11px', background: '#fff' }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {loading && (
              <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>Yükleniyor...</td></tr>
            )}
            {!loading && pagedRows.length === 0 && (
              <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>Kayıt bulunamadı</td></tr>
            )}
            {!loading && pagedRows.map((row, ri) => {
              const isSelected = selectedSet.has(row[rowKey]);
              const customStyle = getRowStyle ? getRowStyle(row) : undefined;
              const bg = customStyle?.background || (isSelected ? '#e8eaf6' : undefined);
              return (
                <tr
                  key={row[rowKey] || ri}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{ cursor: onRowClick ? 'pointer' : undefined, ...customStyle, background: bg }}
                >
                  {selectable && (
                    <td style={{ textAlign: 'center', width: '36px' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleRow(row[rowKey])}
                        style={{ accentColor: '#1a237e', width: '14px', height: '14px', cursor: 'pointer' }} />
                    </td>
                  )}
                  {visibleCols.map((col, ci) => (
                    <td key={col.key || ci} style={wrapText
                      ? { whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.4, verticalAlign: 'top' }
                      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>
                      {col.render ? col.render(col.key ? row[col.key] : row, row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── Alt bilgi + Sayfalama ──────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
        {/* Sol: kayıt bilgisi + satır sayısı seçici */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#666' }}>
          <span>
            {totalRows !== data.length
              ? <><strong style={{ color: '#1a237e' }}>{totalRows}</strong> / {data.length} kayıt (filtrelenmiş)</>
              : <><strong>{data.length}</strong> kayıt</>
            }
            {selectable && selectedRows.length > 0 && (
              <span style={{ marginLeft: '8px', color: '#1a237e', fontWeight: '600' }}>· ✓ {selectedRows.length} seçili</span>
            )}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: '#aaa' }}>Satır:</span>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '2px 6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', color: '#333' }}
            >
              {PAGE_SIZE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sağ: sayfa navigasyonu */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <button onClick={() => setCurrentPage(1)} disabled={safePage === 1} style={pgBtn(safePage === 1)}>«</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={pgBtn(safePage === 1)}>‹</button>
            {pageNumbers.map((p, i) => p === '...'
              ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#aaa', fontSize: '12px' }}>…</span>
              : <button key={p} onClick={() => setCurrentPage(p)} style={pgBtn(false, p === safePage)}>{p}</button>
            )}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={pgBtn(safePage === totalPages)}>›</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages} style={pgBtn(safePage === totalPages)}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}

function pgBtn(disabled, active) {
  return {
    minWidth: '28px', height: '28px', padding: '0 6px',
    border: `1px solid ${active ? '#1a237e' : '#ddd'}`,
    borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '12px', fontWeight: active ? '700' : '400',
    background: active ? '#1a237e' : disabled ? '#f9f9f9' : '#fff',
    color: active ? '#fff' : disabled ? '#bbb' : '#333',
    transition: 'all 0.1s',
  };
}
